import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './DeleveragingEventsChart.css';

const DEFAULT_MODEL = 'Active';
const DEFAULT_VIEW = 'timeline';

const MODEL_CONFIG = {
  Absolute: {
    axisLabel: 'Absolute Return (%)',
    barColor: '#3b82f6',
    eventBarColor: '#1d4ed8',
    highlightColor: '#dbeafe',
  },
  Active: {
    axisLabel: 'Active Return (%)',
    barColor: '#60a5fa',
    eventBarColor: '#2563eb',
    highlightColor: '#dbeafe',
  },
};

const formatPercent = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return `${numericValue.toFixed(digits)}%`;
};

const formatAxisPercent = (value) => formatPercent(value, 0);

const formatMonthYear = (rawDate) => {
  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }
  return parsedDate.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
};

const formatYear = (rawValue) => {
  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }
  return parsedDate.toLocaleDateString(undefined, { year: 'numeric' });
};

const combineLabels = (values) => {
  const seen = new Set();
  const ordered = [];

  values.forEach((value) => {
    if (!value || seen.has(value)) {
      return;
    }
    seen.add(value);
    ordered.push(value);
  });

  return ordered.join(' / ');
};

const buildTimelineRows = (payload, selectedEventIds) =>
  (Array.isArray(payload?.timeline_series) ? payload.timeline_series : [])
    .map((row) => {
      const numericReturn = Number(row?.return);
      const timestamp = row?.date ? new Date(row.date).getTime() : Number.NaN;
      if (!Number.isFinite(numericReturn) || !Number.isFinite(timestamp)) {
        return null;
      }

      const eventIds = Array.isArray(row?.event_ids) ? row.event_ids : [];
      const eventLabels = Array.isArray(row?.event_labels) ? row.event_labels : [];
      const eventShortLabels = Array.isArray(row?.event_short_labels) ? row.event_short_labels : [];
      const selectedEventEntries = eventIds
        .map((id, index) => ({
          id,
          label: eventLabels[index] || id,
          shortLabel: eventShortLabels[index] || eventLabels[index] || id,
        }))
        .filter((entry) => selectedEventIds.has(entry.id));

      return {
        date: row.date,
        timestamp,
        returnPct: numericReturn * 100,
        isEvent: selectedEventEntries.length > 0,
        eventDisplayLabel: combineLabels(selectedEventEntries.map((entry) => entry.shortLabel)),
        eventLabels: selectedEventEntries.map((entry) => entry.label),
      };
    })
    .filter(Boolean);

const buildEventRows = (payload) =>
  (Array.isArray(payload?.events) ? payload.events : [])
    .map((eventRow) => {
      const numericReturn = Number(eventRow?.return);
      if (!Number.isFinite(numericReturn)) {
        return null;
      }

      return {
        id: eventRow?.id || '',
        shortLabel: eventRow?.short_label || eventRow?.label || '',
        label: eventRow?.label || '',
        description: eventRow?.description || '',
        eventDate: eventRow?.event_date || '',
        eventMonthLabel: eventRow?.event_month_label || '',
        mappedPeriodLabel: eventRow?.mapped_period_label || '',
        axisLabel: `${eventRow?.short_label || eventRow?.label || ''}::${eventRow?.event_month_label || ''}`,
        returnPct: numericReturn * 100,
      };
    })
    .filter(Boolean);

const buildEventPeriods = (payload, selectedEventIds) =>
  (Array.isArray(payload?.event_periods) ? payload.event_periods : [])
    .map((eventPeriod, index) => {
      const startTimestamp = eventPeriod?.mapped_period_start
        ? new Date(eventPeriod.mapped_period_start).getTime()
        : Number.NaN;
      const endTimestamp = eventPeriod?.mapped_period_end
        ? new Date(eventPeriod.mapped_period_end).getTime()
        : Number.NaN;

      if (!Number.isFinite(startTimestamp) || !Number.isFinite(endTimestamp)) {
        return null;
      }

      const eventIds = Array.isArray(eventPeriod?.event_ids) ? eventPeriod.event_ids : [];
      const eventShortLabels = Array.isArray(eventPeriod?.event_short_labels)
        ? eventPeriod.event_short_labels
        : [];
      const selectedShortLabels = eventIds
        .map((id, eventIndex) => ({
          id,
          shortLabel: eventShortLabels[eventIndex] || id,
        }))
        .filter((entry) => selectedEventIds.has(entry.id))
        .map((entry) => entry.shortLabel);

      if (!selectedShortLabels.length) {
        return null;
      }

      return {
        id: `${eventPeriod?.mapped_period_end || 'period'}-${index}`,
        startTimestamp,
        endTimestamp,
        eventDisplayLabel: combineLabels(selectedShortLabels),
      };
    })
    .filter(Boolean);

const EventAxisTick = ({ x, y, payload }) => {
  const [label, dateLabel] = String(payload?.value || '').split('::');

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#475467"
        fontSize={12}
        transform="rotate(-22)"
      >
        <tspan x={0} dy={0}>
          {label}
        </tspan>
        <tspan x={0} dy={14}>
          {dateLabel}
        </tspan>
      </text>
    </g>
  );
};

const TimelineTooltip = ({ active, payload, modelLabel }) => {
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  return (
    <div className="deleveraging-chart__tooltip">
      <div className="deleveraging-chart__tooltip-title">{formatMonthYear(point.date)}</div>
      <p className="deleveraging-chart__tooltip-copy">
        {modelLabel} return: {formatPercent(point.returnPct)}
      </p>
      {point.isEvent && point.eventDisplayLabel && (
        <p className="deleveraging-chart__tooltip-copy">
          Event: {point.eventDisplayLabel}
        </p>
      )}
    </div>
  );
};

const EventTooltip = ({ active, payload, modelLabel }) => {
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  return (
    <div className="deleveraging-chart__tooltip">
      <div className="deleveraging-chart__tooltip-title">{point.shortLabel}</div>
      <p className="deleveraging-chart__tooltip-copy">
        Event month: {point.eventMonthLabel || 'N/A'}
      </p>
      <p className="deleveraging-chart__tooltip-copy">
        {modelLabel} return: {formatPercent(point.returnPct)}
      </p>
      {point.description && <p className="deleveraging-chart__tooltip-copy">{point.description}</p>}
    </div>
  );
};

const buildStatItems = (eventRows) => {
  if (!eventRows.length) {
    return [
      { label: 'Average event return', value: 'N/A' },
      { label: 'Best event', value: 'N/A' },
      { label: 'Worst event', value: 'N/A' },
    ];
  }

  const averageReturn = eventRows.reduce((sum, row) => sum + row.returnPct, 0) / eventRows.length;
  const bestEvent = eventRows.reduce((bestSoFar, current) =>
    current.returnPct > bestSoFar.returnPct ? current : bestSoFar,
  );
  const worstEvent = eventRows.reduce((worstSoFar, current) =>
    current.returnPct < worstSoFar.returnPct ? current : worstSoFar,
  );

  return [
    {
      label: 'Average event return',
      value: formatPercent(averageReturn),
    },
    {
      label: 'Best event',
      value: `${bestEvent.shortLabel} ${formatPercent(bestEvent.returnPct)}`,
    },
    {
      label: 'Worst event',
      value: `${worstEvent.shortLabel} ${formatPercent(worstEvent.returnPct)}`,
    },
  ];
};

const DeleveragingEventsChart = ({ data }) => {
  const absolutePayload = data?.Absolute?.[0]?.deleveraging_events || null;
  const activePayload = data?.Active?.[0]?.deleveraging_events || null;
  const preferredModel = useMemo(() => {
    if ((activePayload?.timeline_series || []).length) {
      return 'Active';
    }
    if ((absolutePayload?.timeline_series || []).length) {
      return 'Absolute';
    }
    return DEFAULT_MODEL;
  }, [absolutePayload, activePayload]);

  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [selectedView, setSelectedView] = useState(DEFAULT_VIEW);
  const [selectedEventIds, setSelectedEventIds] = useState(null);
  const [eventMenuAnchor, setEventMenuAnchor] = useState(null);
  const effectiveModel = useMemo(() => {
    if (selectedModel === 'Absolute' && absolutePayload) {
      return 'Absolute';
    }
    if (selectedModel === 'Active' && activePayload) {
      return 'Active';
    }
    return preferredModel;
  }, [absolutePayload, activePayload, preferredModel, selectedModel]);
  const selectedPayload = effectiveModel === 'Absolute' ? absolutePayload : activePayload;
  const config = MODEL_CONFIG[effectiveModel];

  const allEventRows = useMemo(() => buildEventRows(selectedPayload), [selectedPayload]);
  const availableEventIds = useMemo(
    () => allEventRows.map((row) => row.id).filter(Boolean),
    [allEventRows],
  );
  const activeSelectedEventIds = useMemo(() => {
    if (selectedEventIds === null) {
      return new Set(availableEventIds);
    }
    return new Set(selectedEventIds.filter((id) => availableEventIds.includes(id)));
  }, [availableEventIds, selectedEventIds]);
  const timelineRows = useMemo(
    () => buildTimelineRows(selectedPayload, activeSelectedEventIds),
    [activeSelectedEventIds, selectedPayload],
  );
  const eventRows = useMemo(
    () => allEventRows.filter((row) => activeSelectedEventIds.has(row.id)),
    [activeSelectedEventIds, allEventRows],
  );
  const eventPeriods = useMemo(
    () => buildEventPeriods(selectedPayload, activeSelectedEventIds),
    [activeSelectedEventIds, selectedPayload],
  );
  const statItems = useMemo(
    () => buildStatItems(eventRows),
    [eventRows],
  );
  const selectedEventCount = activeSelectedEventIds.size;
  const eventFilterLabel = allEventRows.length
    ? `Events (${selectedEventCount}/${allEventRows.length})`
    : 'Events';
  const eventMenuOpen = Boolean(eventMenuAnchor);

  const handleModelChange = (event, newModel) => {
    if (newModel !== null) {
      setSelectedModel(newModel);
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setSelectedView(newView);
    }
  };

  const handleEventMenuOpen = (event) => {
    setEventMenuAnchor(event.currentTarget);
  };

  const handleEventMenuClose = () => {
    setEventMenuAnchor(null);
  };

  const handleSelectAllEvents = () => {
    setSelectedEventIds(null);
  };

  const handleClearEvents = () => {
    setSelectedEventIds([]);
  };

  const handleToggleEvent = (eventId) => {
    setSelectedEventIds((previousSelection) => {
      const currentSelection = previousSelection === null
        ? availableEventIds
        : previousSelection.filter((id) => availableEventIds.includes(id));

      if (currentSelection.includes(eventId)) {
        return currentSelection.filter((id) => id !== eventId);
      }

      return [...currentSelection, eventId];
    });
  };

  if (!absolutePayload && !activePayload) {
    return (
      <Alert severity="info">
        Deleveraging event analysis will appear once this stage finishes processing.
      </Alert>
    );
  }

  return (
    <div className="deleveraging-chart">
        <div className="deleveraging-chart__controls">
          <div className="deleveraging-chart__control-group">
            <ToggleButtonGroup
              value={effectiveModel}
              exclusive
              onChange={handleModelChange}
              aria-label="Return mode"
              size="small"
            >
              <ToggleButton value="Absolute" aria-label="Absolute return">
                Absolute
              </ToggleButton>
              <ToggleButton value="Active" aria-label="Active return">
                Active
              </ToggleButton>
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={selectedView}
              exclusive
              onChange={handleViewChange}
              aria-label="Deleveraging event view"
              size="small"
            >
              <ToggleButton value="timeline" aria-label="All periods">
                All periods
              </ToggleButton>
              <ToggleButton value="events" aria-label="Deleveraging events only">
                Deleveraging only
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterListIcon />}
            onClick={handleEventMenuOpen}
            disabled={!allEventRows.length}
            aria-haspopup="menu"
            aria-expanded={eventMenuOpen ? 'true' : undefined}
          >
            {eventFilterLabel}
          </Button>
          <Menu
            anchorEl={eventMenuAnchor}
            open={eventMenuOpen}
            onClose={handleEventMenuClose}
            PaperProps={{ className: 'deleveraging-chart__event-menu' }}
          >
            <MenuItem onClick={handleSelectAllEvents} disabled={selectedEventCount === allEventRows.length}>
              Select all
            </MenuItem>
            <MenuItem onClick={handleClearEvents} disabled={selectedEventCount === 0}>
              Clear
            </MenuItem>
            <Divider />
            {allEventRows.map((row) => (
              <MenuItem key={row.id} onClick={() => handleToggleEvent(row.id)} dense>
                <Checkbox
                  checked={activeSelectedEventIds.has(row.id)}
                  size="small"
                  tabIndex={-1}
                />
                <ListItemText
                  primary={row.shortLabel}
                  secondary={row.eventMonthLabel}
                />
              </MenuItem>
            ))}
          </Menu>
        </div>

      <div className="deleveraging-chart__canvas">
        {selectedView === 'timeline' ? (
          timelineRows.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timelineRows} margin={{ top: 24, right: 24, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <ReferenceLine y={0} stroke="#334155" strokeOpacity={0.75} />
                {eventPeriods.map((eventPeriod) => (
                  <ReferenceArea
                    key={eventPeriod.id}
                    x1={eventPeriod.startTimestamp}
                    x2={eventPeriod.endTimestamp}
                    fill={config.highlightColor}
                    fillOpacity={0.9}
                    ifOverflow="extendDomain"
                    label={{
                      value: eventPeriod.eventDisplayLabel,
                      position: 'insideTop',
                      angle: -90,
                      fill: '#475467',
                      fontSize: 11,
                    }}
                  />
                ))}
                <XAxis
                  dataKey="timestamp"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatYear}
                  minTickGap={28}
                />
                <YAxis
                  width={80}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatAxisPercent}
                  label={{
                    value: config.axisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      fill: '#475467',
                      fontSize: 12,
                      textAnchor: 'middle',
                    },
                  }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
                  content={<TimelineTooltip modelLabel={effectiveModel} />}
                />
                <Bar dataKey="returnPct" maxBarSize={14}>
                  {timelineRows.map((row) => (
                    <Cell
                      key={row.date}
                      fill={row.isEvent ? config.eventBarColor : config.barColor}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Box className="deleveraging-chart__empty-state">
              <Alert severity="info">No return history is available for the selected series.</Alert>
            </Box>
          )
        ) : eventRows.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={eventRows} margin={{ top: 16, right: 24, left: 12, bottom: 72 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <ReferenceLine y={0} stroke="#334155" strokeOpacity={0.75} />
              <XAxis
                dataKey="axisLabel"
                interval={0}
                tick={<EventAxisTick />}
                axisLine={false}
                tickLine={false}
                height={84}
              />
              <YAxis
                width={80}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatAxisPercent}
                label={{
                  value: config.axisLabel,
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: '#475467',
                    fontSize: 12,
                    textAnchor: 'middle',
                  },
                }}
              />
              <Tooltip
                  cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
                  content={<EventTooltip modelLabel={effectiveModel} />}
                />
              <Bar dataKey="returnPct" maxBarSize={60}>
                {eventRows.map((row) => (
                  <Cell key={row.id} fill={config.eventBarColor} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box className="deleveraging-chart__empty-state">
            <Alert severity="info">
              {allEventRows.length
                ? 'No deleveraging episodes are selected.'
                : 'No documented deleveraging episodes overlap the selected series.'}
            </Alert>
          </Box>
        )}
      </div>

      <div className="deleveraging-chart__stats-grid">
        {statItems.map((item) => (
          <div key={item.label} className="deleveraging-chart__stat-card">
            <span className="deleveraging-chart__stat-label">{item.label}</span>
            <strong className="deleveraging-chart__stat-value">{item.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeleveragingEventsChart;
